import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Card } from '_common/suite/components';
import { Toggle, Dropdown, Tooltip, TooltipProps } from '@ets/design-system';

import { UserPresentation } from '_common/components';
import LikeToggle from '_common/suite/components/Card/LikeToggle/LikeToggle';
import { RichTextLabel } from '_common/lib/components';

import styles from './ViewReplyCard.module.scss';

export type ViewReplyCardProps = {
  reply: Card.Reply;
  optionsToggleTooltip?: Omit<TooltipProps, 'children'>;
  likeToggleTooltip?: Omit<TooltipProps, 'children'>;
  onStartEdit: () => void;
  voteReply: (payload: { replyId?: string | undefined; currentUserLiked: boolean }) => void;
  deleteReply: (replyId: string) => void;
  canEditReply: (replyId: string) => boolean;
  canDeleteReply: (replyId: string) => boolean;
  canVoteReply: (replyId: string) => boolean;
  legacyModal?: boolean;
  /** ID to be used in unit or automated tests.
   * Will result in 4 different attributes:
   * * ${testId}-view
   * * ${testId}-moreOptions
   * * ${testId}-moreOptions-edit
   * * ${testId}-moreOptions-delete
   */
  testId: string;
};

const ViewReplyCard = ({
  reply,
  optionsToggleTooltip = { visible: false, content: '', testId: 'options' },
  likeToggleTooltip = { visible: false, content: '', testId: 'like' },
  voteReply,
  onStartEdit,
  deleteReply,
  canEditReply,
  canDeleteReply,
  canVoteReply,
  legacyModal,
  testId,
}: ViewReplyCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleDeleteReply = async () => {
    deleteReply(reply.id);
  };

  return (
    <div className={styles.replyCard} data-testid={`${testId}-view`} ref={containerRef}>
      <Card.Header size="medium">
        <Card.Header.Left>
          <UserPresentation
            userId={reply.authorId}
            // @ts-expect-error Uniformize Annotations with Presentation comment type (missing authorName)
            fixedName={!reply.authorId ? (reply.title ?? reply.authorName) : undefined}
            // @ts-expect-error Uniformize Annotations with Presentation comment type (has 'creationDate' instead of 'time')
            creationDate={reply.creationDate ?? reply.time}
          />
        </Card.Header.Left>
        <Card.Header.Right>
          <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
            <Dropdown.Trigger>
              <Tooltip {...optionsToggleTooltip}>
                <Toggle
                  size="m"
                  variant="link"
                  style={{ margin: '0 0 0 auto' }}
                  iconProps={{ icon: 'ellipsis' }}
                  isToggled={isOpen}
                  testId={`${testId}-moreOptions`}
                />
              </Tooltip>
            </Dropdown.Trigger>
            <Dropdown.Content
              testId={`${testId}-options-dropdown`}
              align="end"
              container={containerRef.current ?? undefined}
            >
              <Dropdown.Item
                onClick={onStartEdit}
                disabled={!canEditReply(reply.id)}
                testId={`${testId}-moreOptions-edit`}
              >
                <FormattedMessage id="global.edit" />
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleDeleteReply}
                disabled={!canDeleteReply(reply.id)}
                testId={`${testId}-moreOptions-delete`}
              >
                <FormattedMessage id="global.delete" />
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Root>
        </Card.Header.Right>
      </Card.Header>
      <Card.Body style={{ padding: '0 1rem' }}>
        {reply.application === 'docx' && (
          <div className={styles.imported}>
            <FormattedMessage id="IMPORTED_FROM_WORD_DOCUMENT" />
          </div>
        )}
        <RichTextLabel
          initialContent={reply.content.content}
          testId={testId}
          collapse
          className="**:text-left! **:font-[Arial]! **:text-[12px]! **:text-black!"
        />
      </Card.Body>
      <Card.Footer size="large">
        <div className={styles.footer}>
          <span>
            <LikeToggle
              renderWrapper={(children) => (
                <Tooltip {...likeToggleTooltip}>
                  <span>{children}</span>
                </Tooltip>
              )}
              votes={reply.votes ?? []}
              replyId={reply.id}
              target="reply"
              disabled={!canVoteReply(reply.id)}
              onVote={voteReply}
              testId={testId}
              legacyModal={legacyModal}
            />
          </span>
        </div>
      </Card.Footer>
    </div>
  );
};

export default ViewReplyCard;
